// src/components/ui/AvatarPicker.jsx
// Picker de avatar: upload de arquivo OU captura por câmera, com pré-visualização,
// recorte central automático e compressão WebP (~80KB). 100% client-side.
//
// Uso:
//   <AvatarPicker
//     valor={avatarBase64}
//     nome="Rodrigo Carlos"
//     loading={salvando}
//     onSave={(novoBase64) => ...}
//     onRemove={() => ...}              // se omitido, botão Remover não aparece
//     onErro={(msg) => banner(...)}
//     maxBytes={250 * 1024}             // limite final após compressão
//     tamanhoFinal={400}                // px do quadrado final
//   />
import React, { useEffect, useRef, useState, useCallback } from "react";
import Avatar from "./Avatar";
import { abrirCamera, capturarFoto, pararCamera } from "../../utils/camera";
import { processarImagemAvatar, tamanhoBase64Bytes } from "../../utils/imageProcess";

export default function AvatarPicker({
  valor,
  nome = "",
  loading = false,
  onSave,
  onRemove,
  onErro,
  maxBytes = 250 * 1024,
  tamanhoFinal = 400,
  qualidade = 0.82,
}) {
  const [preview, setPreview] = useState(null);     // dataURL processada aguardando confirmação
  const [stream, setStream]   = useState(null);     // MediaStream ativo
  const [camAberta, setCamAberta] = useState(false);
  const [processando, setProcessando] = useState(false);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  // Quando abrir câmera, plugar o stream no <video>
  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      pararCamera(stream);
    };
  }, []); // eslint-disable-line

  const erro = useCallback((msg) => {
    if (typeof onErro === "function") onErro(msg);
  }, [onErro]);

  // ── Upload de arquivo ───────────────────────────────────────────────
  const handleArquivo = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      erro("Selecione uma imagem (PNG, JPG, WEBP).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      erro("Imagem original muito grande (máx. 8MB).");
      return;
    }
    try {
      setProcessando(true);
      const out = await processarImagemAvatar(file, { size: tamanhoFinal, quality: qualidade });
      if (tamanhoBase64Bytes(out) > maxBytes) {
        // tenta degradar qualidade automaticamente
        const out2 = await processarImagemAvatar(file, { size: tamanhoFinal, quality: 0.65 });
        setPreview(out2);
      } else {
        setPreview(out);
      }
    } catch (e) {
      erro(e?.message || "Não foi possível processar a imagem.");
    } finally {
      setProcessando(false);
    }
  };

  // ── Câmera ──────────────────────────────────────────────────────────
  const abrirCam = async () => {
    try {
      // No fluxo de avatar, queremos preferencialmente a câmera frontal
      let s;
      try {
        s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "user" } },
        });
      } catch {
        // fallback robusto usando o helper que prefere a câmera disponível
        s = await abrirCamera();
      }
      setStream(s);
      setCamAberta(true);
    } catch (e) {
      erro(e?.message || "Não foi possível acessar a câmera.");
    }
  };

  const tirarFoto = async () => {
    if (!videoRef.current) return;
    try {
      setProcessando(true);
      const dataUrl = capturarFoto(videoRef.current, "image/jpeg", 0.92);
      // Para a câmera
      pararCamera(stream);
      setStream(null);
      setCamAberta(false);
      // Processa (crop central + compressão WebP)
      const out = await processarImagemAvatar(dataUrl, { size: tamanhoFinal, quality: qualidade });
      setPreview(out);
    } catch (e) {
      erro(e?.message || "Não foi possível capturar a foto.");
    } finally {
      setProcessando(false);
    }
  };

  const fecharCam = () => {
    pararCamera(stream);
    setStream(null);
    setCamAberta(false);
  };

  // ── Confirmar / cancelar pré-visualização ───────────────────────────
  const confirmar = async () => {
    if (!preview) return;
    try {
      await onSave?.(preview);
      setPreview(null);
    } catch (e) {
      erro(e?.message || "Erro ao salvar.");
    }
  };

  const cancelarPreview = () => setPreview(null);

  // ── Remover avatar atual ────────────────────────────────────────────
  const remover = async () => {
    try {
      await onRemove?.();
    } catch (e) {
      erro(e?.message || "Erro ao remover.");
    }
  };

  const desabilitado = loading || processando;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
      {/* Pré-visualização da nova foto (aguardando confirmação) */}
      {preview ? (
        <>
          <div style={{ position: "relative" }}>
            <img
              src={preview}
              alt="Pré-visualização"
              style={{
                width: 160,
                height: 160,
                borderRadius: "50%",
                objectFit: "cover",
                border: "4px solid #BFDBFE",
                boxShadow: "0 4px 16px rgba(0,0,0,.1)",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: -6,
                right: -6,
                background: "#3B82F6",
                color: "#fff",
                width: 36,
                height: 36,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                boxShadow: "0 2px 8px rgba(0,0,0,.2)",
              }}
            >
              <i className="bi bi-check-lg" />
            </div>
          </div>

          <div style={{ fontSize: 12, color:"var(--gray-500)", textAlign: "center" }}>
            Pré-visualização ({Math.round(tamanhoBase64Bytes(preview) / 1024)} KB).
            <br />
            Confirmar essa foto?
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
            <button
              type="button"
              className="btn btn-success btn-sm"
              disabled={desabilitado}
              onClick={confirmar}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Salvando...
                </>
              ) : (
                <>
                  <i className="bi bi-check2-circle me-1" />
                  Confirmar
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={desabilitado}
              onClick={cancelarPreview}
            >
              <i className="bi bi-arrow-counterclockwise me-1" />
              Tentar outra
            </button>
          </div>
        </>
      ) : camAberta ? (
        // Câmera aberta
        <>
          <div
            style={{
              width: 280,
              height: 280,
              borderRadius: "50%",
              overflow: "hidden",
              border: "4px solid #3B82F6",
              boxShadow: "0 4px 16px rgba(0,0,0,.15)",
              position: "relative",
              background: "#000",
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transform: "scaleX(-1)",
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={processando}
              onClick={tirarFoto}
            >
              <i className="bi bi-camera-fill me-1" />
              Capturar foto
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={processando}
              onClick={fecharCam}
            >
              <i className="bi bi-x-lg me-1" />
              Cancelar
            </button>
          </div>
        </>
      ) : (
        // Estado normal: avatar atual + botões
        <>
          <Avatar
            src={valor}
            nome={nome}
            size={160}
            borderColor="#C7D2FE"
            style={{ boxShadow: "0 4px 16px rgba(0,0,0,.1)", borderWidth: 4 }}
          />

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
            <label
              className="btn btn-primary btn-sm"
              style={{ cursor: desabilitado ? "not-allowed" : "pointer", margin: 0 }}
            >
              <i className="bi bi-upload me-1" />
              {valor ? "Trocar foto" : "Enviar foto"}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: "none" }}
                disabled={desabilitado}
                onChange={handleArquivo}
              />
            </label>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={desabilitado}
              onClick={abrirCam}
            >
              <i className="bi bi-camera me-1" />
              Usar câmera
            </button>
            {valor && onRemove && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                disabled={desabilitado}
                onClick={remover}
              >
                <i className="bi bi-trash3 me-1" />
                Remover
              </button>
            )}
          </div>

          <div style={{ fontSize: 11, color:"var(--gray-400)", textAlign: "center", lineHeight: 1.4 }}>
            PNG, JPG ou WEBP — recorte central automático.
            <br />
            A imagem é otimizada (≈ {Math.round(maxBytes / 1024)}KB) antes do envio.
          </div>

          {processando && (
            <div style={{ fontSize: 11, color:"var(--gray-500)" }}>
              <span className="spinner-border spinner-border-sm me-2" />
              Processando imagem...
            </div>
          )}
        </>
      )}
    </div>
  );
}
